/**
 * Mission Control — the HR Director's personalized Morning Brief
 * (Design-fidelity PR3, rebuilt against the Claude Design reference).
 *
 * Read-only aggregator over M10-M20. Layout:
 *   - Morning Brief header — time-aware greeting + a narrative of
 *     what the AI handled overnight (real ai-activity data).
 *   - KPI cards row — tonal <KPICard>s with the brand-gradient
 *     strip + 7-day sparklines.
 *   - "Needs your attention" — escalation cards for pending
 *     approvals, with the person, the workflow context, and
 *     triage actions that route to the workflow page.
 *   - "AI activity · last 24 hours" — the Concierge's recent feed.
 *   - "Compliance calendar" — iqama / contract / probation
 *     deadlines, color-coded by urgency.
 *
 * Click-throughs go to the underlying workflow pages; the dashboard
 * triages, the workflow pages do the work (commandment 21).
 */

import { useUser } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  ClipboardCheck,
  LogOut,
  Sparkles,
  Users,
  Wallet,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { OnboardingProgressWidget } from "../components/onboarding/OnboardingProgressWidget";
import { EmptyState, KPICard, WorkflowStateBadge } from "../components/ui";
import type { KPITone } from "../components/ui";
import { api } from "../lib/api";
import type {
  AiActivityItem,
  ComplianceAlertItem,
  PendingApprovalItem,
} from "../lib/api";
import { useLegalEntityId } from "../lib/legalEntity";

// --- shared maps -----------------------------------------------------------

// Workflow + compliance label maps are now keyed by i18n catalog keys
// (resolved via t() at render time, not at module load) so they react
// to the locale toggle without a remount.
const _WORKFLOW_LABEL_KEYS: Record<
  PendingApprovalItem["workflow_type"],
  string
> = {
  leave_request: "mission_control.workflow_leave",
  loan_application: "mission_control.workflow_loan",
  excuse_request: "mission_control.workflow_excuse",
  termination: "mission_control.workflow_eos",
};

const _KPI_TONE: Record<string, KPITone> = {
  headcount: "neutral",
  leave_pending: "amber",
  loan_pending: "amber",
  excuse_pending: "amber",
  termination_open: "amber",
  compliance_alerts: "teal",
  ai_escalations: "indigo",
};

const _KPI_ICON: Record<string, LucideIcon> = {
  headcount: Users,
  leave_pending: ClipboardCheck,
  loan_pending: Wallet,
  excuse_pending: AlertCircle,
  termination_open: LogOut,
  compliance_alerts: AlertTriangle,
  ai_escalations: Sparkles,
};

// Backend `card.label` is English-only; map by `card.key` to a
// catalog key the frontend resolves via t(). When a key isn't in
// the map (forward-compatible cards), fall back to the server label.
const _KPI_LABEL_KEYS: Record<string, string> = {
  headcount: "mission_control.kpi_headcount",
  leave_pending: "mission_control.kpi_leave_pending",
  loan_pending: "mission_control.kpi_loan_pending",
  excuse_pending: "mission_control.kpi_excuse_pending",
  termination_open: "mission_control.kpi_termination_open",
  compliance_alerts: "mission_control.kpi_compliance_alerts",
  ai_escalations: "mission_control.kpi_ai_escalations",
};

const _ESCALATION_TRANSITIONS = new Set([
  "route_for_approval",
  "advance_step",
]);

// --- helpers ---------------------------------------------------------------

/**
 * Time-of-day greeting keyed by hour. Caller resolves via t().
 * Keeps the bucket logic pure + testable.
 */
function greetingKeyFor(hour: number): string {
  if (hour < 12) return "mission_control.morning_brief_title_morning";
  if (hour < 18)
    return "mission_control.morning_brief_title_afternoon";
  return "mission_control.morning_brief_title_evening";
}

function initialsOf(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return (parts[0][0] + (parts[1]?.[0] ?? "")).toUpperCase();
}

function urgencyPill(days: number): string {
  if (days <= 14) return "bg-rose-100 text-rose-700";
  if (days <= 30) return "bg-amber-100 text-amber-700";
  return "bg-surface-sunken text-text-secondary";
}

// ===========================================================================
// Morning Brief header
// ===========================================================================

function MorningBrief({
  firstName,
  handled,
  escalated,
}: {
  firstName: string;
  handled: number;
  escalated: number;
}) {
  const { t, i18n } = useTranslation();
  const now = new Date();
  const greetingKey = greetingKeyFor(now.getHours());
  const longDate = now.toLocaleDateString(
    i18n.language === "ar" ? "ar-SA" : undefined,
    {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    },
  );
  const insidePolicy = Math.max(0, handled - escalated);

  return (
    <header className="flex flex-col gap-3 border-b border-border-subtle pb-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1.5">
          <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-brand-blue">
            <Sparkles size={12} className="text-brand-teal" aria-hidden />
            {t("mission_control.morning_brief_label")} · {longDate}
          </span>
          <h1 className="text-3xl font-bold text-brand-deep">
            {t(greetingKey, { name: firstName })}.
          </h1>
          {handled > 0 ? (
            <p className="max-w-2xl text-sm leading-relaxed text-text-secondary">
              {t("mission_control.brief_handled_intro")}{" "}
              <strong className="font-semibold text-brand-deep">
                {handled}
              </strong>{" "}
              {handled === 1
                ? t("mission_control.brief_request_singular")
                : t("mission_control.brief_request_plural")}{" "}
              —{" "}
              <strong className="font-semibold text-brand-deep">
                {insidePolicy}
              </strong>{" "}
              {t("mission_control.brief_inside_policy_connector")}{" "}
              <strong className="font-semibold text-brand-deep">
                {escalated}
              </strong>{" "}
              {escalated === 1
                ? t("mission_control.brief_pending_singular")
                : t("mission_control.brief_pending_plural")}
            </p>
          ) : (
            <p className="max-w-2xl text-sm leading-relaxed text-text-secondary">
              {t("mission_control.brief_all_quiet")}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-md border border-border-subtle bg-surface-default px-2.5 py-1.5 text-xs font-medium text-text-secondary">
            <CalendarClock size={13} aria-hidden />{" "}
            {t("mission_control.today")}
          </span>
          <Link
            to="/operations/approvals"
            className="inline-flex items-center gap-1 rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg hover:bg-brand-hover"
          >
            {t("mission_control.start_review_cta")} →
          </Link>
        </div>
      </div>
    </header>
  );
}

// ===========================================================================
// "Needs your attention" — escalation cards
// ===========================================================================

function EscalationCard({ item }: { item: PendingApprovalItem }) {
  const { t } = useTranslation();
  const workflowLabel = t(_WORKFLOW_LABEL_KEYS[item.workflow_type]);
  const name =
    item.employee_name_en ??
    item.employee_code ??
    t("mission_control.employee");
  return (
    <div className="relative flex flex-col gap-3 overflow-hidden rounded-xl border border-border-subtle bg-surface-default p-4">
      <span
        aria-hidden
        className="absolute inset-x-0 top-0 h-0.5 bg-brand-gradient-h"
      />
      <div className="flex items-center justify-between">
        <span className="rounded bg-surface-sunken px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
          {workflowLabel}
        </span>
        <WorkflowStateBadge state={item.state} />
      </div>
      <div className="flex items-center gap-2.5">
        <span
          aria-hidden
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ai-bg text-xs font-semibold text-ai-deep"
        >
          {initialsOf(item.employee_name_en)}
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-text-primary">
            {name}
          </p>
          <p className="text-xs text-text-tertiary">
            {workflowLabel}
            {item.employee_code ? ` · ${item.employee_code}` : ""}
          </p>
        </div>
      </div>
      <p className="flex items-start gap-1.5 text-xs leading-relaxed text-text-secondary">
        <Sparkles
          size={12}
          className="mt-0.5 shrink-0 text-brand-teal"
          aria-hidden
        />
        <span>{item.summary}</span>
      </p>
      <div className="flex items-center gap-2 pt-0.5">
        <Link
          to={item.detail_link}
          className="inline-flex flex-1 items-center justify-center rounded-md border border-border-subtle px-2.5 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-sunken hover:text-text-primary"
        >
          {t("mission_control.deny")}
        </Link>
        <Link
          to={item.detail_link}
          className="inline-flex flex-1 items-center justify-center gap-1 rounded-md bg-brand px-2.5 py-1.5 text-xs font-medium text-brand-fg hover:bg-brand-hover"
        >
          {t("mission_control.review_cta")} →
        </Link>
      </div>
    </div>
  );
}

function NeedsAttention({ le }: { le: string | undefined }) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["mc-pending-approvals", le ?? "_all_"],
    queryFn: () =>
      api.missionControlPendingApprovals({
        legalEntityId: le,
        limitPerType: 6,
      }),
    refetchInterval: 60_000,
  });
  const items = q.data ?? [];
  return (
    <section className="flex flex-col gap-3">
      <header className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-base font-semibold text-text-primary">
          {t("mission_control.needs_attention")}
          {items.length > 0 && (
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-600 px-1.5 text-[11px] font-semibold text-white">
              {items.length}
            </span>
          )}
        </h2>
      </header>
      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <ErrorPanel message={(q.error as Error).message} />
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-border-subtle bg-surface-default">
          <EmptyState
            icon={CheckCircle2}
            headline={t("mission_control.no_escalations_headline")}
            description={t("mission_control.no_escalations_body")}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => (
            <EscalationCard
              key={`${item.workflow_type}:${item.id}`}
              item={item}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// ===========================================================================
// "AI activity · last 24 hours" feed
// ===========================================================================

function AiActivityFeed({ items }: { items: AiActivityItem[] }) {
  const { t } = useTranslation();
  return (
    <section className="flex flex-col gap-3">
      <h2 className="flex items-center gap-1.5 text-base font-semibold text-text-primary">
        <Sparkles size={14} className="text-brand-teal" aria-hidden />
        {t("mission_control.ai_activity_title_24h")}
      </h2>
      <div className="rounded-xl border border-border-subtle bg-surface-default">
        {items.length === 0 ? (
          <EmptyState
            icon={Sparkles}
            headline={t("mission_control.no_ai_activity_headline")}
            description={t("mission_control.no_ai_activity_body")}
          />
        ) : (
          <ul role="list" className="divide-y divide-border-subtle">
            {items.slice(0, 8).map((ev) => {
              const escalated = _ESCALATION_TRANSITIONS.has(
                ev.transition,
              );
              return (
                <li
                  key={ev.id}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-xs"
                >
                  {escalated ? (
                    <AlertCircle
                      size={14}
                      className="shrink-0 text-amber-600"
                      aria-hidden
                    />
                  ) : (
                    <CheckCircle2
                      size={14}
                      className="shrink-0 text-emerald-600"
                      aria-hidden
                    />
                  )}
                  <span className="min-w-0 flex-1 truncate text-text-primary">
                    <span className="font-medium">
                      {ev.workflow_type}
                    </span>{" "}
                    <span className="text-text-secondary">
                      {ev.transition} · {ev.from_state ?? "∅"} →{" "}
                      {ev.to_state}
                    </span>
                  </span>
                  <span className="shrink-0 font-mono text-[10px] text-text-tertiary">
                    {ev.occurred_at.slice(11, 16)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

// ===========================================================================
// "Compliance calendar" widget
// ===========================================================================

const _COMPLIANCE_KIND_KEYS: Record<
  ComplianceAlertItem["kind"],
  string
> = {
  iqama_expiry: "mission_control.compliance_kind_iqama",
  contract_expiry: "mission_control.compliance_kind_contract",
  probation_ending: "mission_control.compliance_kind_probation",
};

function ComplianceCalendar({ le }: { le: string | undefined }) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["mc-compliance", le ?? "_all_"],
    queryFn: () => api.missionControlComplianceAlerts(le),
    refetchInterval: 5 * 60_000,
  });
  const alerts = q.data?.alerts ?? [];
  return (
    <section className="flex flex-col gap-3">
      <h2 className="flex items-center gap-1.5 text-base font-semibold text-text-primary">
        <CalendarClock
          size={14}
          className="text-text-secondary"
          aria-hidden
        />
        {t("mission_control.compliance_calendar_title_v2")}
      </h2>
      <div className="rounded-xl border border-border-subtle bg-surface-default">
        {q.isLoading ? (
          <Loading />
        ) : q.error ? (
          <ErrorPanel message={(q.error as Error).message} />
        ) : alerts.length === 0 ? (
          <EmptyState
            icon={CheckCircle2}
            headline={t("mission_control.no_upcoming_deadlines_headline")}
            description={t("mission_control.no_upcoming_deadlines_body")}
          />
        ) : (
          <ul role="list" className="divide-y divide-border-subtle">
            {alerts.slice(0, 8).map((alert) => (
              <li
                key={`${alert.kind}:${alert.employee_id}:${alert.due_date}`}
              >
                <Link
                  to={alert.detail_link}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-xs hover:bg-surface-sunken"
                >
                  <span className="rounded bg-surface-sunken px-1.5 py-0.5 text-[10px] font-semibold uppercase text-text-secondary">
                    {t(_COMPLIANCE_KIND_KEYS[alert.kind])}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-text-primary">
                    {alert.employee_name_en}{" "}
                    <span className="text-text-tertiary">
                      ({alert.employee_code})
                    </span>
                  </span>
                  <span
                    className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium ${urgencyPill(
                      alert.days_until,
                    )}`}
                  >
                    {alert.days_until}
                    {t("mission_control.days_short")}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

// ===========================================================================
// MissionControlPage
// ===========================================================================

export function MissionControlPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();
  const { user } = useUser();
  const firstName =
    user?.firstName?.trim() || t("mission_control.fallback_name");

  const headlines = useQuery({
    queryKey: ["mc-headlines", le ?? "_all_"],
    queryFn: () => api.missionControlHeadlines(le),
    refetchInterval: 60_000,
  });
  const aiActivity = useQuery({
    queryKey: ["mc-ai-activity", le ?? "_all_"],
    queryFn: () => api.missionControlAiActivity(le, 24),
    refetchInterval: 60_000,
  });

  if (headlines.isLoading) return <Loading />;
  if (headlines.error)
    return <ErrorPanel message={(headlines.error as Error).message} />;
  if (!headlines.data) return null;

  const summary = aiActivity.data?.summary;

  return (
    <div className="flex flex-col gap-7">
      {/* M27 PR 1 — pinned full-width at the very top while
          onboarding is < 100% complete. Hides automatically once
          go-live stamps the journey complete. */}
      <OnboardingProgressWidget />

      <MorningBrief
        firstName={firstName}
        handled={summary?.handled_count ?? 0}
        escalated={summary?.escalated_count ?? 0}
      />

      {/* KPI cards — tonal, brand-gradient strip, 7-day sparklines. */}
      <section
        aria-label={t("mission_control.key_metrics_label")}
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
      >
        {headlines.data.cards.map((card) => (
          <KPICard
            key={card.key}
            tone={_KPI_TONE[card.key] ?? "neutral"}
            icon={_KPI_ICON[card.key] ?? AlertCircle}
            label={
              _KPI_LABEL_KEYS[card.key]
                ? t(_KPI_LABEL_KEYS[card.key])
                : card.label
            }
            count={card.count}
            subtitle={card.secondary}
            trend={card.trend}
            linkTo={card.link_to}
          />
        ))}
      </section>

      <NeedsAttention le={le} />

      {/* AI activity feed + compliance calendar, side by side. */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <AiActivityFeed items={aiActivity.data?.items ?? []} />
        <ComplianceCalendar le={le} />
      </div>
    </div>
  );
}
